import React, {useMemo} from "react"
import PropTypes from "prop-types";
import Tr from "translations/Translations";
import VatType from "constants/VatType";
import {makeStyles} from "@material-ui/core/styles";
import BoldTypography from "components/BoldTypography";
import Typography from "components/Typography";
import {VARIANT_CODE} from "components/popup/newCriteria/constants/criteria.variants";
import OfferYesNoInput from "./OfferYesNoInput";
import OfferTextInput from "./OfferTextInput";
import GridBlock from "components/GridBlock";
import OfferNumberInput from "./OfferNumberInput";
import OfferPriceInput from "./OfferPriceInput";
import {PriceType} from "constants/PriceTypes";
import OfferListInput from "./OfferListInput";
import TextInput from "components/TextInput";
import getObjectValue from "libs/getObjectValue";
import WorkableIconEditButton from "components/button/WorkableIconEditButton";
import WorkableIconDeleteButton from "components/button/WorkableIconDeleteButton";
import OfferUnits from "./OfferUnits"
import clsx from "clsx";

const useStyle = makeStyles(theme => ({
    root: {
        border: `solid 2px ${theme.palette.primary.main}`,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        marginTop: theme.spacing()
    },
    marginTop1: {
        marginTop: theme.spacing(1)
    },
    marginTop2: {
        marginTop: theme.spacing(2)
    },
    text: {
        width: 800
    }
}))

const offerInput = (uiType) => {
    switch (uiType) {
        case VARIANT_CODE.YESNO:
            return OfferYesNoInput;
        case VARIANT_CODE.NUMBER:
        case VARIANT_CODE.INTERVAL:
            return OfferNumberInput;
        case VARIANT_CODE.PRICE:
            return OfferPriceInput;
        case VARIANT_CODE.LIST:
            return OfferListInput;
        default:
            return OfferTextInput;
    }
}


function OfferInput(
    {
        id,
        criterion,
        priceType,
        formikProps,
        disabled,
    }) {

    const classes = useStyle();
    const customQuantity = getObjectValue(id + ".quantity", formikProps.values, null) !== null;

    const Input = offerInput(criterion.criterium.uiType);
    const inpDisabled = useMemo(() => disabled || getObjectValue(id + ".disabled", formikProps.initialValues, false), [id, formikProps.initialValues, disabled])
    const quantityDisabled = useMemo(() => getObjectValue(id + ".quantityDisabled", formikProps.initialValues, false), [id, formikProps.initialValues])
    const canEditQuantity = useMemo(() => getObjectValue(id + ".criterium.allowQuantityEditInOffer", formikProps.initialValues, false), [id, formikProps.initialValues])

    const turnOnEditQuantity = () => {
        formikProps.setFieldValue(id + '.quantity', {
            value: getObjectValue(id + '.criterium.quantity.value', formikProps.values, null),
            uom: getObjectValue(id + ".criterium.quantity.uom", formikProps.values, null)
        });
    }

    const turnOffEditQuantity = () => {
        formikProps.setFieldValue(id + '.quantity', null);
    }

    const formatQuantity = (value) => {
        if (!value) {
            return '';
        }

        return value.toLocaleString(undefined, {minimumFractionDigits: 3, maximumFractionDigits: 3});
    }

    return (<div className={classes.root}>
        <BoldTypography>{criterion.criterium.name}</BoldTypography>
        {!!criterion.criterium.description && <Typography>{criterion.criterium.description}</Typography>}


        {!!criterion.criterium.quantity && !customQuantity &&
        <Typography className={clsx({[classes.marginTop1]: !canEditQuantity})}>
            {`${Tr('quantity')}: ${formatQuantity(criterion.criterium.quantity.value)} ${criterion.criterium.uom}`}
            {!disabled && canEditQuantity && <WorkableIconEditButton onClick={() => turnOnEditQuantity()}/>}
        </Typography>}

        {!!criterion.criterium.quantity && customQuantity &&
        <GridBlock>
            <Typography className={classes.marginTop1}>
                {`${Tr('quantity')}: `}
            </Typography>
            <OfferUnits
                id={id}
                formikProps={formikProps}
                disabled={inpDisabled || quantityDisabled}
            />
            {!inpDisabled && !quantityDisabled && <WorkableIconDeleteButton onClick={() => turnOffEditQuantity()}/>}
        </GridBlock>
        }


        {criterion.criterium.weight !== null && <Typography>{`${Tr('weight')}: ${criterion.criterium.weight}`}</Typography>}
        {!!criterion.criterium.uom && !criterion.criterium.quantity && <Typography>{`${Tr('measure_unit')}: ${criterion.criterium.uom}`}</Typography>}
        {!!criterion.criterium.baseItemId && <Typography>{Tr('position_type.optional.offer')}</Typography>}
        {!!criterion.criterium.offerValuesPredefined && <Typography>{Tr('offerSourceType.initiator.offer')}</Typography>}
        {!criterion.criterium.offerValuesPredefined && !!criterion.criterium.offerVatPredefined && <Typography>{Tr('offerVatPredefined.offer')}</Typography>}
        <GridBlock
            className={classes.marginTop2}
            justify="flex-end"
        >
            <Input
                formikProps={formikProps}
                id={id}
                criterium={criterion.criterium}
                listValues={criterion.criterium.listValues}
                priceType={priceType}
                quantity={getObjectValue(id + '.quantity', formikProps.values, criterion.criterium.quantity)}
                disabled={inpDisabled || (!!criterion.criterium.baseItemId && !!criterion.criterium.copyValuesFromBaseItem)}
            />
        </GridBlock>
        {
            criterion.criterium.showComment &&
            <GridBlock
                justify="flex-end"
            >
                <TextInput
                    className={classes.text}
                    id={`${id}.comment`}
                    formikProps={formikProps}
                    label={Tr('position_additional_comment')}
                    disabled={inpDisabled}
                    multiline
                />
            </GridBlock>
        }
    </div>)


}

OfferInput.propTypes = {
    id: PropTypes.string.isRequired,
    priceType: PropTypes.oneOf(Object.values(PriceType)).isRequired,
    criterion: PropTypes.shape({
        criterium: PropTypes.shape({
            identity: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            description: PropTypes.string,
            uiType: PropTypes.string.isRequired,
            uom: PropTypes.string,
            listValues: PropTypes.arrayOf(PropTypes.string.isRequired),
            weight: PropTypes.number,
            showComment: PropTypes.bool.isRequired,
            quantity: PropTypes.shape({
                value: PropTypes.number.isRequired,
                uom: PropTypes.string.isRequired
            })
        }),
        value: PropTypes.any,
        vatType: PropTypes.oneOf(Object.values(VatType)),
        vatValue: PropTypes.any,
        grossValue: PropTypes.any,
        comment: PropTypes.string,
        quantity: PropTypes.shape({
            value: PropTypes.any,
            uom: PropTypes.string
        })
    })

}

export default OfferInput;